import React, { useState } from "react";
import { useAuthStore } from "../store/authStore";
import { useUIStore } from "../store/uiStore";
import {
  LogOut, Menu, User, X, Settings,
  Bell, Palette, ChevronRight, ShieldCheck, Mail
} from "lucide-react";
import { useThemeStore } from "../store/themeStore";
import { logoutRequest } from "../service/authService";
import { useNavigate } from "react-router-dom";
import { Button, MenuButton } from "./ButtonComponents";
import { getTurkishRoleName } from "../types/role";

export const Navbar: React.FC = () => {
  const { user, logout, refreshToken } = useAuthStore();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const { theme, setTheme } = useThemeStore();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const navigate = useNavigate();

  const toggleSettings = () => setIsSettingsOpen(!isSettingsOpen);

  const handleLogout = async () => {
    try {
      if (navigator.onLine && refreshToken) {
        await logoutRequest(refreshToken);
      }
    } catch (error) {
      console.warn("Çıkış işlemi sırasında API hatası oluştu.");
    } finally {
      setIsSettingsOpen(false);
      logout();
      navigate("/login");
    }
  };

  const roleName = getTurkishRoleName(user?.role.name || "");

  const themeOptions = [
    { id: 'light', label: 'Klasik', bg: '#ffffff', text: '#171717', border: '#e5e5e5', tooltip: "Klasik beyaz olmazsa olmaz." },
    { id: 'slate', label: 'Nordic', bg: '#f1f5f9', text: '#0f172a', border: '#cbd5e1', tooltip: "Bu da hiç fena değil :)" },
    { id: 'rose', label: 'Pudra', bg: '#fffbfb', text: '#4a3737', border: '#fce7f3', tooltip: "Sıcak ve yumuşak tonlar." },
    { id: 'dark', label: 'Koyu', bg: '#121212', text: '#f0f0f0', border: '#444444', tooltip: "Gece çalışanlara özel." },
    { id: 'ocean', label: 'Okyanus', bg: '#0e2a47', text: '#e9f5ff', border: '#1e3a5f', tooltip: "Hanım bunu sevdi." },
    { id: 'midnight', label: 'Gece', bg: '#020617', text: '#f8fafc', border: '#334155', tooltip: "Bana göre en iyisi! 🌙" },
    { id: 'purple', label: 'Moruş', bg: '#1a0b2e', text: '#f4e8ff', border: '#3b2257', tooltip: "Kızımın en sevdiği mor tonu! 💜" },
    { id: 'emerald', label: 'Zombi', bg: '#064e3b', text: '#ecfdf5', border: '#065f46', tooltip: "Kızımın özel zombi yeşili! 🧟‍♂️" }
  ];

  return (
    <>
      {/* --- NAVBAR --- */}
      <div className="h-16  bg-[var(--navbar-bg)] border-b border-[var(--border)] flex items-center justify-between px-4 md:px-6 sticky top-0 z-40 select-none shadow-sm">
        <div className="flex items-center gap-3 h-16">
          <button 
            className="p-2 rounded-xl hover:bg-[var(--bg-alt)] transition-colors text-[var(--text)]" 
            onClick={toggleSidebar}
          >
            <Menu size={22} />
          </button>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] text-[var(--text)] opacity-50 font-black uppercase tracking-widest hidden sm:block">Hoş geldin,</span>
            <span className="text-sm font-bold text-[var(--text)]">{user?.name}</span>
          </div>
        </div>

        {/* Sağ: Profil & Bildirim */}
        <div className="flex items-center gap-2">
                  {/* 
          <button className="p-2 text-[var(--text)] opacity-40 hover:opacity-100 transition-all relative">
            <Bell size={20} />
            <span className="absolute top-2.5 right-2.5 w-2 h-2 bg-red-500 rounded-full border border-[var(--navbar-bg)]"></span>
          </button>
 */}
          <button 
            onClick={toggleSettings} 
            className="flex items-center gap-2 p-1 md:pr-3 rounded-full bg-[var(--bg-alt)] border border-[var(--border)] hover:border-[var(--primary)] transition-all active:scale-95"
          >
            <div className="bg-[var(--primary)] text-white w-7 h-7 rounded-full flex items-center justify-center shadow-md">
              <User size={14} fill="currentColor" />
            </div>
            <span className="text-[11px] font-black text-[var(--text)] uppercase hidden md:block">{user?.name?.split(' ')[0].toLowerCase()}</span>
          </button>
        </div>
      </div>

      {/* --- SIDEBAR DRAWER --- */}
    {/* --- ARKA PLAN KARARTMA (OVERLAY) --- */}
<div 
  className={`fixed inset-0 bg-black/20 backdrop-blur-sm z-50 transition-opacity duration-500 ${
    isSettingsOpen ? "opacity-100" : "opacity-0 pointer-events-none"
  }`} 
  onClick={() => setIsSettingsOpen(false)} 
/>

{/* --- SAĞ PANEL (WIDGET) --- */}
<div className={`
  fixed z-[51] transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]
  /* Konumlandırma: Sağ üst ve alt köşeden boşluklu */
  top-0 right-0 h-full
  sm:top-3 sm:right-3 sm:h-[calc(100vh-24px)] 
  /* Genişlik: Mobilde tam, Masaüstünde sabit 340px */
  w-full sm:w-[340px] 
  /* Tasarım */
  bg-[var(--card)] sm:rounded-[2.5rem] shadow-[0_20px_50px_rgba(0,0,0,0.3)] 
  flex flex-col overflow-hidden border-l sm:border border-[var(--border)]
  /* Animasyon: Sağa kayarak gizlen */
  ${isSettingsOpen ? "translate-x-0 opacity-100" : "translate-x-full opacity-0"}
`}>
  
  {/* Üst: Profil Kartı (bg-alt ile ayrılmış) */}
  <div className="p-6 bg-[var(--bg-alt)]/50 border-b border-[var(--border)] relative">
    <button 
      onClick={() => setIsSettingsOpen(false)} 
      className="absolute top-4 right-5 p-2 text-[var(--text)] opacity-30 hover:opacity-100 hover:bg-[var(--card)] rounded-full transition-all"
    >
      <X size={18} />
    </button>
    
    <div className="flex items-center gap-4 mt-2">
      <div className="h-14 w-14 rounded-2xl bg-[var(--primary)] flex items-center justify-center text-white text-xl font-black shadow-lg shadow-[var(--primary)]/20 rotate-3 transition-transform hover:rotate-0">
          {user?.name?.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
          <p className="font-black text-[var(--text)] text-lg leading-tight uppercase italic truncate">{user?.name}</p>
          <p className="text-[10px] text-[var(--text)] opacity-40 font-bold uppercase tracking-widest mt-0.5 truncate">{user?.email}</p>
      </div>
    </div>
  </div>

  {/* Orta: İçerik (Kaydırılabilir) */}
  <div className="flex-1 overflow-y-auto p-5 space-y-6">
      {/* Menü Grubu */}
      <div className="space-y-1">
          <p className="text-[9px] font-black text-[var(--text)] opacity-20 uppercase tracking-[0.3em] ml-2 mb-3">Hesap Yönetimi</p>
          <MenuButton icon={<Settings size={18}/>} label="Profil Ayarlarım" onClick={() => { navigate("/account-setting"); setIsSettingsOpen(false); }} />
          <MenuButton icon={<Bell size={18}/>} label="Bildirimler" badge="Yeni" />
      </div>

      {/* Tema Seçimi (Kızının İsimleri ve Tooltip) */}
      <div className="p-4 bg-[var(--bg-alt)]/40 rounded-3xl border border-[var(--border)]">
          <div className="flex items-center justify-between mb-4 px-1">
            <div className="flex items-center gap-2 text-[var(--text)] font-black text-[10px] uppercase opacity-40">
                <Palette size={14} />
                <span>Temalar</span>
            </div>
            <span className="text-[9px] font-bold text-[var(--primary)] uppercase tracking-tighter italic bg-[var(--primary)]/10 px-2 py-0.5 rounded-full">{theme}</span>
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {themeOptions.map((t) => (
              <div key={t.id} className="relative group/tooltip">
                <button
                  onClick={() => setTheme(t.id as any)}
                  style={{ backgroundColor: t.bg, borderColor: t.border }}
                  className={`w-full h-11 rounded-xl border flex items-center justify-center transition-all relative overflow-hidden
                    ${theme === t.id ? 'ring-2 ring-[var(--primary)] shadow-md scale-[1.05] z-10' : 'opacity-70 hover:opacity-100 hover:scale-[1.02]'}`}
                >
                   <span style={{ color: t.text }} className="text-[10px] font-black uppercase tracking-tight">{t.label}</span>
                   {theme === t.id && <div className="absolute bottom-0 left-0 right-0 h-1 bg-[var(--primary)]" />}
                </button>

                {t.tooltip && (
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[9px] px-2 py-1 rounded-md shadow-2xl opacity-0 group-hover/tooltip:opacity-100 pointer-events-none transition-opacity whitespace-nowrap z-[60] border border-white/10 font-bold">
                    {t.tooltip}
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 rotate-45"></div>
                  </div>
                )}
              </div>
            ))}
          </div>
      </div>
  </div>

  {/* Alt: Çıkış Yap */}
  <div className="p-5 border-t border-[var(--border)]">
      <button 
        onClick={handleLogout} 
        className="w-full h-12 flex items-center justify-center gap-2 rounded-2xl bg-red-500/10 text-red-500 font-black text-[10px] uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all duration-300 group"
      >
        <LogOut size={16} className="group-hover:-translate-x-1 transition-transform" />
        Güvenli Çıkış
      </button>
  </div>
</div>
    </>
  );
};